import { ReactNode } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';

import { useStorage } from '@/utils/storage/useStorage.util';

interface QuerySplitLayoutProps {
  leftContent: ReactNode;
  rightContent: ReactNode;
  storageKey: string;
  defaultSizes?: [number, number];
  testId?: string;
}

/**
 * QuerySplitLayout - Reusable split-pane layout component
 *
 * Provides horizontal split-pane with:
 * - Persistent sizing via useStorage (tenant-scoped localStorage)
 * - Draggable resize handle
 * - Configurable default sizes
 *
 * Used by:
 * - QueryWriter (JSON editor + results)
 * - QuerySplitEditor (coming soon UI + results)
 * - QuerySplitViewer (coming soon UI + results)
 */
export const QuerySplitLayout = ({
  leftContent,
  rightContent,
  storageKey,
  defaultSizes = [60, 40],
  testId = 'query-split-layout',
}: QuerySplitLayoutProps) => {
  // Persistent split ratio with tenant-scoped storage
  // Pattern from QueryWriter.tsx lines 67-70
  const [sizes, setSizes] = useStorage<number[]>(
    { key: storageKey },
    defaultSizes
  );

  return (
    <PanelGroup
      direction="horizontal"
      className="h-full"
      onLayout={sizes => setSizes(sizes)}
      data-testid={`${testId}-panel-group`}
    >
      {/* Left pane: 60% default, 30% minimum */}
      <Panel
        defaultSize={sizes[0]}
        minSize={30}
        data-testid={`${testId}-left-panel`}
      >
        {leftContent}
      </Panel>

      {/* Draggable divider with hover state */}
      <PanelResizeHandle
        className="bg-chariot-stroke hover:bg-chariot-brand-primary w-1 cursor-col-resize transition-colors"
        data-testid={`${testId}-resize-handle`}
      />

      {/* Right pane: 40% default, 20% minimum */}
      <Panel
        defaultSize={sizes[1]}
        minSize={20}
        data-testid={`${testId}-right-panel`}
      >
        {rightContent}
      </Panel>
    </PanelGroup>
  );
};
